import { wrapApiCall } from '@/lib/axios'
import type { ReportLang } from '@/utils/helper/reportLang'
import type { AxiosInstance } from 'axios'

/** Every `/reports/*\/workbook` endpoint takes the range plus the language. */
type DownloadWorkbookParams = {
  startDate: string
  endDate: string
  lang: ReportLang
}

/** The package-membership workbook also takes the CSV's own filters. */
type DownloadPackageMembershipWorkbookParams = DownloadWorkbookParams & {
  packageTypeIds?: number[]
  membershipStatus?: string[]
  saleId?: number
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export const ReportsService = (client: AxiosInstance) => {
  /** One shape for every workbook download: query in, binary out. */
  const downloadWorkbook = async (url: string, params: Record<string, unknown>): Promise<Blob> => {
    const { data } = await wrapApiCall<Blob>(() =>
      client.get(url, {
        params,
        responseType: 'blob',
        headers: { Accept: XLSX_MIME },
      })
    )
    return data
  }

  return {
    downloadClassBookingReportWorkbook(params: DownloadWorkbookParams): Promise<Blob> {
      return downloadWorkbook('/reports/class-bookings/workbook', params)
    },
    downloadSwimmingBookingReportWorkbook(params: DownloadWorkbookParams): Promise<Blob> {
      return downloadWorkbook('/reports/swimming-bookings/workbook', params)
    },
    downloadPtBookingReportWorkbook(params: DownloadWorkbookParams): Promise<Blob> {
      return downloadWorkbook('/reports/pt-bookings/workbook', params)
    },
    downloadPackageMembershipReportWorkbook({
      packageTypeIds,
      membershipStatus,
      ...range
    }: DownloadPackageMembershipWorkbookParams): Promise<Blob> {
      // Both list filters travel as the comma-separated form the DTO parses;
      // an empty selection is left off entirely so it never narrows the file.
      return downloadWorkbook('/reports/package-memberships/workbook', {
        ...range,
        ...(packageTypeIds?.length ? { packageTypeIds: packageTypeIds.join(',') } : {}),
        ...(membershipStatus?.length ? { membershipStatus: membershipStatus.join(',') } : {}),
      })
    },
    downloadLeadOpportunityReportWorkbook(params: DownloadWorkbookParams): Promise<Blob> {
      return downloadWorkbook('/reports/lead-opportunity/workbook', params)
    },
    downloadSalePerformanceReportWorkbook(params: DownloadWorkbookParams): Promise<Blob> {
      return downloadWorkbook('/reports/sale-performance/workbook', params)
    },
    /**
     * The check-in workbook. Its range params are `from`/`to`, not the
     * `startDate`/`endDate` the `/reports/*` family uses.
     */
    downloadCheckinReportXlsx(params: {
      from: string
      to: string
      lang: ReportLang
    }): Promise<Blob> {
      return downloadWorkbook('/checkin/export', params)
    },
  }
}
